/**
 * Base class for widgets used in the Grid Layout
 */
export class Widget {
    static widgetList = {};
  
    /**
     * Create a new widget
     * @param {string} type - Unique identifier for the widget
     * @param {string} name - Display name
     * @param {string} category - Category for grouping widgets
     * @param {Function} render - Render function for the widget
     * @param {Array} options - Configuration options for the widget
     * @param {Object} gridSettings - Grid placement settings
     */
    constructor(type, name, category, render, options = [], gridSettings = {}) {
      this.type = type;
      this.name = name;
      this.category = category;
      this.render = render;
      this.options = options;
      this.gridSettings = gridSettings;
  
      // Default grid settings for Bootstrap's grid system
      // Bootstrap uses a 12-column grid system
      const defaultSettings = {
        minWidth: 1,            // Minimum 1/12 width
        maxWidth: 12,           // Maximum full width (12/12)
        minHeight: 1,           // Minimum 1 unit of height
        maxHeight: 12,          // Maximum 12 units of height
        defaultWidth: 6,        // Default to half-width (6/12)
        defaultHeight: 2,       // Default 2 units of height
        // Default responsive behavior
        xs: 12,                 // Full width on mobile (12/12)
        sm: null,               // null means use the default calculation in GridLayout
        md: null,
        lg: null,
        xl: null
      };
  
      this.gridSettings = { ...defaultSettings, ...gridSettings };
  
      // Register the widget in the static list
      Widget.widgetList[type] = this;
    }
  
    /**
     * Transform placeholders in widget options
     * @param {Object} data - Guide data including answers
     * @param {Object} options - Widget options that may contain placeholders
     * @returns {Object} - Transformed options
     */
    static transformValuesInObject(data, options) {
      const transformedOptions = { ...options };
  
      // Function to recursively process values
      const processValue = (value) => {
        if (typeof value === 'string' && value.includes('{{') && value.includes('}}')) {
          // Extract placeholder keys
          const placeholderRegex = /\{\{([^}]+)\}\}/g;
          let matches;
          let transformedValue = value;
  
          while ((matches = placeholderRegex.exec(value)) !== null) {
            const fullMatch = matches[0];
            const placeholderPath = matches[1].trim();
            
            // Navigate the data object to get the value
            const pathParts = placeholderPath.split('.');
            let currentValue = data;
            
            for (const part of pathParts) {
              if (currentValue && typeof currentValue === 'object' && part in currentValue) {
                currentValue = currentValue[part];
              } else {
                currentValue = null;
                break;
              }
            }
            
            // Replace the placeholder with the actual value
            if (currentValue !== null && currentValue !== undefined) {
              transformedValue = transformedValue.replace(fullMatch, String(currentValue));
            }
          }
          
          return transformedValue;
        } else if (Array.isArray(value)) {
          return value.map(item => processValue(item));
        } else if (value && typeof value === 'object') {
          const result = {};
          for (const [key, val] of Object.entries(value)) {
            result[key] = processValue(val);
          }
          return result;
        }
        
        return value;
      };
      
      // Process each option
      for (const [key, value] of Object.entries(options)) {
        transformedOptions[key] = processValue(value);
      }
      
      return transformedOptions;
    }
  
    /**
     * Check if a given type is a registered widget
     * @param {string} type - The widget type to check
     * @returns {boolean} - True if the type is a registered widget
     */
    static isRegisteredWidget(type) {
      return Object.prototype.hasOwnProperty.call(Widget.widgetList, type);
    }

    /**
     * Export widget definitions for use in the portal
     * @returns {Array} - Array of widget definitions formatted for export
     */
    static exportWidgets() {
      // Convert the widgetList object to an array of widget definitions
      return Object.values(Widget.widgetList).map(widget => ({
        type: widget.type,
        name: widget.name,
        category: widget.category,
        options: widget.options,
        gridSettings: widget.gridSettings
      }));
    }
}